
export class Decoder {
  constructor(readonly jso: any) { }

  keyedDecoder(): KeyedDecoder {
    if (typeof this.jso !== 'object') {
      throw new Error(`Expected object, got ${this.jso}`)
    }
    return new KeyedDecoder(this.jso)
  }

  static decodeInstanceFromLocalStorage<T>(
    key: string,
    decode: (decoder: Decoder) => T,
    fallback: T | (() => T)
  ): T {
    try {
      const json = localStorage.getItem(key)
      if (!json) {
        throw new Error(`no value for ${key} in localStorage`)
      }

      const jso = JSON.parse(json)
      const decoder = new Decoder(jso)
      return decode(decoder)
    } catch (error) {
      console.log(`error while loading ${key} from localStorage: ${error}`)
      return fallback instanceof Function ? fallback() : fallback
    }
  }
}

export class KeyedDecoder {
  constructor(readonly jso: { [key: string]: any }) { }

  decodeBoolean(key: string, fallback?: boolean): boolean {
    const value = this.jso[key]
    if (typeof value !== 'boolean') {
      if (fallback !== undefined) {
        return fallback
      }
      throw new Error(`Expected boolean at ${key}, got ${value}`)
    }
    return value
  }

  decodeNumber(key: string, fallback?: number): number {
    const value = this.jso[key]
    if (typeof value !== 'number') {
      if (fallback !== undefined) {
        return fallback
      }
      throw new Error(`Expected number at ${key}, got ${value}`)
    }
    return value
  }

  decodeString(key: string, fallback?: string): string {
    const value = this.jso[key]
    if (typeof value !== 'string') {
      if (fallback !== undefined) {
        return fallback
      }
      throw new Error(`Expected string at ${key}, got ${value}`)
    }
    return value
  }

  decodeInstance<T>(key: string, decode: (decoder: Decoder) => T, fallback?: () => T): T {
    const value = this.jso[key]
    if (value == null) {
      if (fallback !== undefined) {
        return fallback()
      }
      throw new Error(`no value for ${key} in ${this.jso}`)
    }
    return decode(new Decoder(value))
  }

  decodeArray<T>(key: string, decode: (decoder: Decoder) => T, fallback?: () => T[]) {
    const array = this.jso[key]
    if (!Array.isArray(array)) {
      if (fallback !== undefined) {
        return fallback()
      }
      throw new Error(`Expected array at ${key}, got ${array}`)
    }

    return array.map(element => decode(new Decoder(element)))
  }

}
